import { useState, useEffect, useRef, useCallback } from "react";
import { useRole } from "@/contexts/RoleContext";
import { supabase, executeWithRetry } from "@/lib/supabase";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Package, ShoppingCart, CheckCircle2, Copy, Printer, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface CatalogItem {
  id: string;
  name: string;
  unit: string;
  created_at: string;
}

interface MaterialRequest {
  id: string;
  location_id: string | null;
  items: Array<{
    item_id: string;
    name: string;
    requested_qty: number;
    unit: string;
  }>;
  notes: string | null;
  status: string;
  created_at: string;
  location_address?: string;
}

interface GroupedRequest {
  item_id: string;
  item_name: string;
  unit: string;
  total_quantity: number;
  locations: Array<{
    location_id: string;
    location_address: string;
    quantity: number;
    request_id: string;
  }>;
}

export function WarehouseManagement() {
  const { currentOrgId } = useRole();
  const [catalogItems, setCatalogItems] = useState<CatalogItem[]>([]);
  const [materialRequests, setMaterialRequests] = useState<MaterialRequest[]>([]);
  const [groupedRequests, setGroupedRequests] = useState<GroupedRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isAddProductOpen, setIsAddProductOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | null>(null);
  const [isShoppingListOpen, setIsShoppingListOpen] = useState(false);
  const [newProductName, setNewProductName] = useState("");
  const [newProductUnit, setNewProductUnit] = useState("szt");
  
  // CRITICAL: Ref do śledzenia poprzedniego currentOrgId - zapobiega resetowaniu przy remount
  const previousOrgIdRef = useRef<string | null>(null);
  const hasInitializedRef = useRef(false);

  const fetchCatalog = useCallback(async () => {
    if (!currentOrgId) return;
    try {
      const { data, error } = await supabase
        .from("cleaning_catalog")
        .select("*")
        .eq("org_id", currentOrgId)
        .order("name", { ascending: true });

      if (error) throw error;
      setCatalogItems(data || []);
    } catch (error) {
      console.error("Error fetching catalog:", error);
      toast.error("Błąd pobierania katalogu");
    }
  }, [currentOrgId]);

  const fetchMaterialRequests = useCallback(async () => {
    if (!currentOrgId) return;
    try {
      const { data, error } = await supabase
        .from("material_requests")
        .select(`
          id,
          location_id,
          items,
          notes,
          status,
          created_at,
          cleaning_locations(address)
        `)
        .eq("org_id", currentOrgId)
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      if (error) throw error;

      const requests = (data || []).map((req: any) => ({
        id: req.id,
        location_id: req.location_id,
        items: Array.isArray(req.items) ? req.items : [],
        notes: req.notes ?? null,
        status: req.status,
        created_at: req.created_at,
        location_address: req.cleaning_locations?.address || "Nieznana lokalizacja",
      }));

      setMaterialRequests(requests);

      // Group requests by product
      const grouped = new Map<string, GroupedRequest>();
      const locationMap = new Map<string, string>();

      requests.forEach((req) => {
        if (req.location_id) {
          locationMap.set(req.location_id, req.location_address);
        }
        req.items.forEach((item: any) => {
          const itemName = item.name || item.product_name || item.label || item.item_name || "";
          const key = item.item_id || item.id || itemName || String(Math.random());
          if (!grouped.has(key)) {
            grouped.set(key, {
              item_id: item.item_id || item.id || "",
              item_name: itemName || "Produkt bez nazwy",
              unit: item.unit || "szt",
              total_quantity: 0,
              locations: [],
            });
          }
          const group = grouped.get(key)!;
          group.total_quantity += item.requested_qty || item.quantity || item.qty || 0;
          if (req.location_id) {
            const existingLocation = group.locations.find(
              (loc) => loc.location_id === req.location_id
            );
            if (existingLocation) {
              existingLocation.quantity += item.requested_qty || item.quantity || item.qty || 0;
            } else {
              group.locations.push({
                location_id: req.location_id,
                location_address: req.location_address,
                quantity: item.requested_qty || item.quantity || item.qty || 0,
                request_id: req.id,
              });
            }
          }
        });
      });

      setGroupedRequests(Array.from(grouped.values()));
    } catch (error) {
      console.error("Error fetching material requests:", error);
      toast.error("Błąd pobierania zapotrzebowań");
    }
  }, [currentOrgId]);

  useEffect(() => {
    // CRITICAL: Zapobieganie resetowaniu danych przy remount komponentu
    // Jeśli currentOrgId się nie zmienił i dane już zostały załadowane, nie resetuj
    const orgIdChanged = previousOrgIdRef.current !== currentOrgId;
    
    if (!currentOrgId) {
      // Brak orgId - wyczyść dane
      setCatalogItems([]);
      setMaterialRequests([]);
      setGroupedRequests([]);
      previousOrgIdRef.current = null;
      hasInitializedRef.current = false;
      return;
    }
    
    // Jeśli orgId się nie zmienił i już zainicjalizowano, nie resetuj
    if (!orgIdChanged && hasInitializedRef.current) {
      console.log('[WarehouseManagement] currentOrgId nie zmienił się - zachowuję istniejące dane');
      return;
    }
    
    // OrgId się zmienił lub pierwsza inicjalizacja - pobierz dane
    console.log('[WarehouseManagement] Pobieranie danych dla orgId:', currentOrgId, orgIdChanged ? '(zmiana orgId)' : '(pierwsza inicjalizacja)');
    previousOrgIdRef.current = currentOrgId;
    setIsLoading(true);
    
    Promise.all([
      executeWithRetry(() => fetchCatalog(), { isReadOperation: true }),
      executeWithRetry(() => fetchMaterialRequests(), { isReadOperation: true }),
    ])
      .catch((error) => {
        console.error("[WarehouseManagement] Błąd ładowania magazynu:", error);
      })
      .finally(() => {
        setIsLoading(false);
        hasInitializedRef.current = true;
      });
  }, [currentOrgId, fetchCatalog, fetchMaterialRequests]);

  // Bulletproof Handler: validate before spinner, try-catch-finally so spinner always turns off.
  const handleAddProduct = useCallback(async () => {
    if (!currentOrgId) {
      toast.error("Błąd: Brak identyfikatora organizacji. Odśwież stronę.");
      return;
    }
    if (!newProductName.trim()) {
      toast.error("Wprowadź nazwę produktu");
      return;
    }

    setIsActionLoading(true);
    try {
      const { error } = await executeWithRetry(
        async () =>
          await supabase
            .from("cleaning_catalog")
            .insert({
            org_id: currentOrgId,
            name: newProductName.trim(),
            unit: newProductUnit,
          }),
        { isReadOperation: false }
      );

      if (error) throw error;

      toast.success("Produkt dodany do katalogu");
      setNewProductName("");
      setNewProductUnit("szt");
      setIsAddProductOpen(false);
      await executeWithRetry(() => fetchCatalog(), { isReadOperation: true });
    } catch (error: any) {
      console.error("Error adding product:", error);
      toast.error(error?.message ?? "Wystąpił błąd podczas dodawania produktu.");
    } finally {
      setIsActionLoading(false);
    }
  }, [currentOrgId, newProductName, newProductUnit, fetchCatalog]);

  const handleDeleteProduct = useCallback(async () => {
    if (!currentOrgId) {
      toast.error("Błąd: Brak identyfikatora organizacji. Odśwież stronę.");
      return;
    }
    if (!productToDelete) return;

    setIsActionLoading(true);
    try {
      const { error } = await executeWithRetry(
        async () =>
          await supabase
            .from("cleaning_catalog")
            .delete()
            .eq("id", productToDelete)
            .eq("org_id", currentOrgId),
        { isReadOperation: false }
      );

      if (error) throw error;

      toast.success("Produkt usunięty z katalogu");
      setProductToDelete(null);
      setIsDeleteDialogOpen(false);
      await executeWithRetry(() => fetchCatalog(), { isReadOperation: true });
    } catch (error: any) {
      console.error("Error deleting product:", error);
      toast.error(error?.message ?? "Wystąpił błąd podczas usuwania produktu.");
    } finally {
      setIsActionLoading(false);
    }
  }, [currentOrgId, productToDelete, fetchCatalog]);

  const handleMarkAsDelivered = useCallback(async () => {
    if (!currentOrgId) {
      toast.error("Błąd: Brak identyfikatora organizacji. Odśwież stronę.");
      return;
    }

    const requestIds = materialRequests.map((req) => req.id);
    if (requestIds.length === 0) {
      toast.info("Brak zapotrzebowań do oznaczenia");
      return;
    }

    setIsActionLoading(true);
    try {
      const { error } = await executeWithRetry(
        async () =>
          await supabase
            .from("material_requests")
            .update({ status: "completed" })
            .in("id", requestIds)
            .eq("org_id", currentOrgId)
            .eq("status", "pending"),
        { isReadOperation: false }
      );

      if (error) throw error;

      toast.success(`Oznaczono ${requestIds.length} zapotrzebowań jako dostarczone`);
      await executeWithRetry(() => fetchMaterialRequests(), { isReadOperation: true });
    } catch (error: any) {
      console.error("Error marking as delivered:", error);
      toast.error(error?.message ?? "Wystąpił błąd podczas oznaczania jako dostarczone.");
    } finally {
      setIsActionLoading(false);
    }
  }, [currentOrgId, materialRequests, fetchMaterialRequests]);

  const generateShoppingList = () => {
    return groupedRequests
      .map((item) => `${item.item_name}: ${item.total_quantity} ${item.unit}`)
      .join("\n");
  };

  const handleCopyShoppingList = () => {
    const list = generateShoppingList();
    navigator.clipboard.writeText(list);
    toast.success("Lista zakupowa skopiowana do schowka");
  };

  const handlePrintShoppingList = () => {
    const list = generateShoppingList();
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Lista Zakupowa</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              h1 { margin-bottom: 20px; }
              pre { font-size: 14px; line-height: 1.6; }
            </style>
          </head>
          <body>
            <h1>Lista Zakupowa</h1>
            <pre>${list}</pre>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Magazyn</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">Ładowanie...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Katalog Produktów */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Katalog Produktów</CardTitle>
              <CardDescription>
                Zarządzaj firmowym katalogiem produktów dostępnych w systemie
              </CardDescription>
            </div>
            <Button onClick={() => setIsAddProductOpen(true)} disabled={isActionLoading}>
              {isActionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Dodaj produkt
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {catalogItems.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              Brak produktów w katalogu. Dodaj pierwszy produkt.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nazwa produktu</TableHead>
                  <TableHead>Jednostka miary</TableHead>
                  <TableHead className="w-[100px]">Akcje</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {catalogItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>{item.unit}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={isActionLoading}
                        onClick={() => {
                          setProductToDelete(item.id);
                          setIsDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Aktywne Zapotrzebowania */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Aktywne Zapotrzebowania</CardTitle>
              <CardDescription>
                Zgrupowane zapotrzebowania na produkty z różnych obiektów
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setIsShoppingListOpen(true)}
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                Generuj listę zakupową
              </Button>
              <Button onClick={handleMarkAsDelivered} disabled={isActionLoading}>
                {isActionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Oznacz jako dostarczone
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {groupedRequests.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              Brak aktywnych zapotrzebowań
            </p>
          ) : (
            <div className="space-y-4">
              {groupedRequests.map((group) => (
                <Card key={group.item_id || group.item_name}>
                  <CardHeader>
                    <CardTitle className="text-lg">
                      {group.item_name}: {group.total_quantity} {group.unit}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">
                        Lokalizacje:
                      </p>
                      <ul className="list-disc list-inside space-y-1">
                        {group.locations.map((loc, idx) => (
                          <li key={idx} className="text-sm">
                            {loc.location_address}: {loc.quantity} {group.unit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lista zamówień (per request) z notatkami */}
      {materialRequests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Szczegóły zamówień</CardTitle>
            <CardDescription>
              Produkty i notatki z każdego zamówienia
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {materialRequests.map((request) => (
                <div
                  key={request.id}
                  className="border border-border/60 rounded-lg p-4 space-y-2"
                >
                  <p className="text-sm font-medium text-muted-foreground">
                    {request.location_address}
                  </p>
                  {request.items && request.items.length > 0 ? (
                    <div className="space-y-1">
                      {request.items.map((item: any, i: number) => (
                        <div key={i} className="flex justify-between items-center text-sm">
                          <span className="font-medium">
                            {item.name || item.product_name || item.label || item.item_name || "Produkt bez nazwy"}
                          </span>
                          <span className="text-muted-foreground ml-2">
                            x{item.quantity ?? item.qty ?? item.requested_qty ?? 0}
                            {item.unit ? ` ${item.unit}` : ""}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Brak produktów</p>
                  )}
                  {request.notes && (
                    <div className="mt-2 text-sm bg-muted/50 p-2 rounded-md border border-border/50">
                      <span className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        Notatka:
                      </span>
                      <p className="text-foreground mt-0.5">{request.notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog: Dodaj produkt */}
      <Dialog open={isAddProductOpen} onOpenChange={setIsAddProductOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dodaj produkt do katalogu</DialogTitle>
            <DialogDescription>
              Dodaj nowy produkt do firmowego katalogu
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="product-name">Nazwa produktu</Label>
              <Input
                id="product-name"
                value={newProductName}
                onChange={(e) => setNewProductName(e.target.value)}
                placeholder="np. Płyn do podłóg"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="product-unit">Jednostka miary</Label>
              <Select
                value={newProductUnit}
                onValueChange={setNewProductUnit}
              >
                <SelectTrigger id="product-unit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="szt">szt (sztuki)</SelectItem>
                  <SelectItem value="litr">litr</SelectItem>
                  <SelectItem value="kg">kg</SelectItem>
                  <SelectItem value="m">m (metry)</SelectItem>
                  <SelectItem value="m²">m² (metry kwadratowe)</SelectItem>
                  <SelectItem value="opak">opak (opakowanie)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddProductOpen(false)}>
              Anuluj
            </Button>
            <Button onClick={handleAddProduct} disabled={isActionLoading}>
              {isActionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Dodaj
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Lista zakupowa */}
      <Dialog open={isShoppingListOpen} onOpenChange={setIsShoppingListOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Lista Zakupowa</DialogTitle>
            <DialogDescription>
              Podsumowanie zapotrzebowań na produkty
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-muted p-4 rounded-lg">
              <pre className="whitespace-pre-wrap font-mono text-sm">
                {generateShoppingList()}
              </pre>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCopyShoppingList}>
              <Copy className="h-4 w-4 mr-2" />
              Kopiuj
            </Button>
            <Button variant="outline" onClick={handlePrintShoppingList}>
              <Printer className="h-4 w-4 mr-2" />
              Drukuj
            </Button>
            <Button onClick={() => setIsShoppingListOpen(false)}>Zamknij</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert Dialog: Usuń produkt */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Usunąć produkt?</AlertDialogTitle>
            <AlertDialogDescription>
              Czy na pewno chcesz usunąć ten produkt z katalogu? Ta operacja
              nie może być cofnięta. Produkty przypisane do obiektów pozostaną,
              ale nie będą już powiązane z katalogiem.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteProduct} disabled={isActionLoading}>
              Usuń
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
